/**
 * API service using RTK Query for data fetching
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  CategoryInfo,
  CategoryPlaybook,
  SpecCard,
  SpecInfo,
  ListResponse,
  PaginatedResponse,
  ErrorResponse,
  PropertyListResponse,
  PropertyListParams,
  PropertyRegistryDetail,
  EnstruLookupEntry,
  Oktru2LookupEntry,
} from '@/types/api';

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';

// Base query with error handling
const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  prepareHeaders: (headers) => {
    headers.set('content-type', 'application/json');
    return headers;
  },
});

// Enhanced base query with error transformation
const baseQueryWithErrorHandling = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);
  
  if (result.error) {
    // Transform error response
    const errorData = result.error.data as ErrorResponse;
    if (errorData?.message) {
      result.error = {
        ...result.error,
        data: errorData,
      };
    }
  }
  
  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Category', 'Playbook', 'Spec', 'Property'],
  endpoints: (builder) => ({

    // Get all categories from static CDN file (fast)
    getStaticCategories: builder.query<CategoryInfo[], void>({
      queryFn: async () => {
        try {
          // Fetch from CDN-cached static file
          const response = await fetch('/data/categories-index.json');
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          // Extract entries array and map field names to match CategoryInfo interface
          const categories = (data.entries || []).map((entry: any) => ({
            id: entry.category_code,
            name: entry.category_name_en || entry.category_name_ru || '',
            name_en: entry.category_name_en,
            name_ru: entry.category_name_ru,
            keywords: entry.keywords || { en: [], ru: [] },
            latest_version: entry.latest_version,
            path: entry.directory_path,
            json_path: entry.json_path,
            property_count: entry.property_count || 0,
            last_updated: entry.last_modified,
          }));
          return { data: categories };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
      providesTags: ['Category'],
      // Cache for 1 hour
      keepUnusedDataFor: 3600,
    }),

    // Get categories with pagination (slower, for compatibility)
    getCategories: builder.query<
      PaginatedResponse<CategoryInfo>,
      { page?: number; per_page?: number } | void
    >({
      query: (params) => ({
        url: '/GetPlaybooks',
        params: params || {},
      }),
      providesTags: (_result, _error, params) => [
        { type: 'Category', id: `page-${params?.page || 1}-${params?.per_page || 50}` }
      ],
    }),

    // Get all specs from static CDN file (fast)
    getStaticSpecs: builder.query<SpecInfo[], void>({
      queryFn: async () => {
        try {
          // Fetch from CDN-cached static file
          const response = await fetch('/data/specs-index.json');
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          // Extract entries array from index structure
          const specs = data.entries || [];
          return { data: specs };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
      providesTags: ['Spec'],
      // Cache for 1 hour
      keepUnusedDataFor: 3600,
    }),

    getEnstruLookup: builder.query<Record<string, EnstruLookupEntry>, void>({
      queryFn: async () => {
        try {
          const response = await fetch('/data/enstru-lookup.json');
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
      keepUnusedDataFor: 86400,
    }),

    getOktru2Lookup: builder.query<Record<string, Oktru2LookupEntry>, void>({
      queryFn: async () => {
        try {
          const response = await fetch('/data/oktru2-lookup.json');
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
      keepUnusedDataFor: 86400,
    }),

    getEnstruToOktru2: builder.query<Record<string, string[]>, void>({
      queryFn: async () => {
        try {
          const response = await fetch('/data/enstru-to-oktru2.json');
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
      keepUnusedDataFor: 86400,
    }),

    // Get specific playbook
    getPlaybook: builder.query<CategoryPlaybook, { category: string; version?: string }>({
      query: ({ category, version }) => ({
        url: `/playbook/${category}`,
        params: version ? { version } : {},
      }),
      providesTags: (_result, _error, { category }) => [
        { type: 'Playbook', id: category },
      ],
    }),

    // Get specific spec
    getSpec: builder.query<SpecCard, string>({
      query: (lotId) => `/spec/${lotId}`,
      providesTags: (_result, _error, lotId) => [
        { type: 'Spec', id: lotId },
      ],
    }),

    // List specs with filtering
    listSpecs: builder.query<
      ListResponse<SpecInfo>,
      {
        category?: string;
        page?: number;
        per_page?: number;
        search?: string;
      }
    >({
      query: ({ category, page = 1, per_page = 100, search }) => ({
        url: '/specs',
        params: {
          ...(category && { category }),
          page,
          per_page,
          ...(search && { search }),
        },
      }),
      providesTags: ['Spec'],
      // Keep previous data while fetching new
      keepUnusedDataFor: 60,
    }),

    // Get playbook versions (derived from getCategories for now)
    getPlaybookVersions: builder.query<string[], string>({
      query: (category) => `/playbook/${category}/versions`,
      providesTags: (_result, _error, category) => [
        { type: 'Playbook', id: `${category}-versions` },
      ],
    }),

    // === Property Registry ===

    // List properties with server-side search, filter, sort, pagination
    listProperties: builder.query<PropertyListResponse, PropertyListParams>({
      query: (params) => ({
        url: '/properties',
        params: {
          ...(params.search && { search: params.search }),
          ...(params.data_type && { data_type: params.data_type }),
          ...(params.deprecated !== undefined && { deprecated: String(params.deprecated) }),
          ...(params.sort_by && { sort_by: params.sort_by }),
          ...(params.sort_dir && { sort_dir: params.sort_dir }),
          ...(params.enstru_code && { enstru_code: params.enstru_code }),
          ...(params.enstru_classifier && { enstru_classifier: params.enstru_classifier }),
          ...(params.oktru2_code && { oktru2_code: params.oktru2_code }),
          ...(params.include_enstru_stats && { include_enstru_stats: 'true' }),
          ...(params.include_oktru2_stats && { include_oktru2_stats: 'true' }),
          ...(params.include_abc && { include_abc: 'true' }),
          top: params.top || 50,
          skip: params.skip || 0,
        },
      }),
      providesTags: ['Property'],
      keepUnusedDataFor: 300,
    }),

    // Get single property by ID
    getProperty: builder.query<PropertyRegistryDetail, string>({
      query: (propertyId) => `/property/${propertyId}`,
      providesTags: (_result, _error, propertyId) => [
        { type: 'Property', id: propertyId },
      ],
      keepUnusedDataFor: 300,
    }),

  }),
});

export const {
  useGetStaticCategoriesQuery,
  useGetCategoriesQuery,
  useGetStaticSpecsQuery,
  useGetEnstruLookupQuery,
  useGetOktru2LookupQuery,
  useGetEnstruToOktru2Query,
  useGetPlaybookQuery,
  useGetSpecQuery,
  useListSpecsQuery,
  useGetPlaybookVersionsQuery,
  useListPropertiesQuery,
  useGetPropertyQuery,
} = api;
